import { useState } from 'react'
import { Archive, Code2, Download, FileIcon, FileSignature, FileSpreadsheet, FileText, Folder, Image, Music, Presentation, Video, Eye, Percent } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import { Document } from '../classes/Document'
import { apiURL } from '../../../../utilities/URLs'
import { account } from '../../../../classes/Account'
import { refreshAccessToken } from '../../../../utilities/extensions'

export default function DocumentRow({ _document }) {
  const { applicationId } = useParams()
  const navigate = useNavigate()

  const [documentObj] = useState(new Document(_document))

  const handleDownloadDocument = async () => {
    const documentResponse = await fetch(`${apiURL}/api/aws/`, {
      method: 'POST',
      headers: {
        'Authorization': account.accessToken,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        name: documentObj.name,
        url: documentObj.documentURL
      })
    })

    const documentData = await documentResponse.json()
    if (documentData.response.error) {
      if (documentData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handleDownloadDocument() : navigate('/', { state: { expired: true } })
      } else
        console.error(documentData.response.error)
    } else
      window.open(documentData.response.signedURL, '_blank')
  }

  function formatFileSize(size) {
    if (size < 1024)
      return `${size} B`
    const kb = size / 1024
    if (kb < 1024)
      return `${kb.toFixed(1)} KB`
    const mb = kb / 1024
    if (mb < 1024)
      return `${mb.toFixed(1)} MB`
    const gb = mb / 1024
    return `${gb.toFixed(1)} GB`
  }

  const getIconByMimetype = (mimetype) => {
    if (!mimetype) return <FileIcon className='w-5 h-5 text-gray-400' />
    if (mimetype.startsWith('image/')) return <Image className='w-5 h-5 text-blue-500 ' />
    if (mimetype.startsWith('video/')) return <Video className='w-5 h-5 text-purple-500 ' />
    if (mimetype.startsWith('audio/')) return <Music className='w-5 h-5 text-green-500 ' />
    if (mimetype === 'application/pdf') return <FileSignature className='w-5 h-5 text-red-500' />
    if (mimetype.includes('word')) return <FileText className='w-5 h-5 text-indigo-500 ' />
    if (mimetype.includes('spreadsheet') || mimetype.includes('excel')) return <FileSpreadsheet className='w-5 h-5 text-green-600 ' />
    if (mimetype.includes('presentation') || mimetype.includes('powerpoint')) return <Presentation className='w-5 h-5 text-orange-500 ' />
    if (mimetype.startsWith('application/json') || mimetype.startsWith('text/html') || mimetype.includes('javascript')) return <Code2 className='w-5 h-5 text-gray-700 ' />
    if (mimetype === 'inode/directory') return <Folder className='w-5 h-5 text-yellow-600 ' />
    if (mimetype.includes('zip') || mimetype.includes('tar') || mimetype.includes('rar')) return <Archive className='w-5 h-5 text-pink-600 ' />
    return <FileIcon className='w-5 h-5 text-gray-400 ' />
  }

  return (
    <li
      key={documentObj.documentId}
      className='w-full border border-gray-200 dark:border-slate-700 rounded-xl p-4 bg-white dark:bg-slate-800 shadow-sm hover:shadow-md transition-all flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 animate-in slide-in-from-bottom-2 fade-in duration-300 fill-mode-both'
    >
      <div className='flex flex-row items-start sm:items-center gap-4 min-w-0 w-full sm:w-auto'>
        <div className='w-12 h-12 shrink-0 bg-slate-50 dark:bg-slate-700/50 flex items-center justify-center rounded-xl border border-gray-100 dark:border-slate-700 shadow-sm'>
          {getIconByMimetype(documentObj.mimetype)}
        </div>
        <div className='min-w-0 flex flex-col'>
          <>
            <div className='flex flex-row items-center gap-2'>
              <p className='font-medium text-gray-900 dark:text-slate-100'>{documentObj.name}</p>
              <span
                className={`
                  px-2 py-0.5 rounded-full text-xs font-medium
                  ${documentObj.status === 'Accepted'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                    : documentObj.status === 'Rejected'
                      ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                      : 'bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-slate-300'
                  }
                `}
              >
                {documentObj.status}
              </span>
            </div>
            <p className='text-sm text-gray-500 dark:text-slate-400'>{documentObj.type} - {formatFileSize(documentObj.size)} - {documentObj.mimetype}</p>
            <p className='text-xs text-gray-400 dark:text-slate-500 mt-2'>Uploaded: {documentObj.timestamp.toLocaleString()}</p>
          </>
        </div>
      </div>
      <div className='flex items-center gap-3 sm:gap-4 w-full sm:w-auto mt-4 sm:mt-0 pt-4 sm:pt-0 border-t sm:border-0 border-gray-100/60 dark:border-slate-700 justify-end flex-shrink-0'>
        {documentObj.type === 'agreement' && (
          <button onClick={() => navigate(`/applications/${applicationId}/documents/original-agreement`)} className='p-2 bg-gray-50 dark:bg-slate-700/50 hover:bg-orange-50 dark:hover:bg-orange-500/10 border border-gray-100 dark:border-slate-700 text-orange-600 dark:text-orange-400 hover:border-orange-200 dark:hover:border-orange-500/20 rounded-lg transition-colors' title='Edit Agreement'>
            <Percent className='w-4 h-4' />
          </button>
        )}
        <button onClick={() => handleDownloadDocument(false)} className='p-2 bg-gray-50 dark:bg-slate-700/50 hover:bg-blue-50 dark:hover:bg-blue-500/10 border border-gray-100 dark:border-slate-700 text-blue-600 dark:text-blue-400 hover:border-blue-200 dark:hover:border-blue-500/20 rounded-lg transition-colors' title='Preview'>
          <Eye className='w-4 h-4' />
        </button>
        <button onClick={() => handleDownloadDocument(true)} className='p-2 bg-gray-50 dark:bg-slate-700/50 hover:bg-blue-50 dark:hover:bg-blue-500/10 border border-gray-100 dark:border-slate-700 text-blue-600 dark:text-blue-400 hover:border-blue-200 dark:hover:border-blue-500/20 rounded-lg transition-colors' title='Download'>
          <Download className='w-4 h-4' />
        </button>
      </div>
    </li>
  )
}